import React, { useEffect, useState } from 'react';
import { Save, Mail, Send, FileText, Euro, Calendar, Scale, Package, ClipboardList, ShieldCheck, Users } from 'lucide-react';
import { authApi } from '../services/api';
import { useTour } from '../hooks/useTour';
import { settingsTourSteps } from '../tours/settingsTour';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import VertragsvorlagenEditor from '../components/Einstellungen/VertragsvorlagenEditor';
import AvailabilitySettings from '../components/Einstellungen/AvailabilitySettings';
import LegalSettings from '../components/Einstellungen/LegalSettings';
import PackageTemplatesEditor from '../components/Einstellungen/PackageTemplatesEditor';
import NoteTemplateEditor from '../components/Einstellungen/NoteTemplateEditor';
import SecuritySettings from '../components/Einstellungen/SecuritySettings';
import TeamManagement from '../components/Einstellungen/TeamManagement';
import FeatureGate from '../components/FeatureGate';
import { Skeleton } from '../components/Skeleton';
import './Pages.css';

interface Profile {
  first_name: string; last_name: string; email: string; phone: string; title: string;
  business_name: string; address_line1: string; address_line2: string; zip_code: string; city: string; country: string;
  tax_number: string; vat_id: string; bank_name: string; iban: string; bic: string;
  smtp_host: string; smtp_port: string; smtp_user: string; smtp_password: string; smtp_from_email: string; smtp_from_name: string;
}

interface InvoiceSettings {
  is_kleinunternehmer: boolean; company_type: string;
  invoice_prefix: string; offer_prefix: string; next_invoice_number: string; next_offer_number: string;
  default_payment_days: string; default_intro_text: string; default_outro_text: string;
  // Mahnwesen
  dunning_grace_days: string; dunning_reminder_days: string;
  dunning_level1_days: string; dunning_level2_days: string; dunning_level3_days: string;
  dunning_fee_level2: string; dunning_fee_level3: string;
}

const emptyInvoiceSettings: InvoiceSettings = {
  is_kleinunternehmer: false, company_type: '', invoice_prefix: 'RE', offer_prefix: 'AN',
  next_invoice_number: '1', next_offer_number: '1', default_payment_days: '14', default_intro_text: '', default_outro_text: '',
  dunning_grace_days: '3', dunning_reminder_days: '7',
  dunning_level1_days: '14', dunning_level2_days: '28', dunning_level3_days: '42',
  dunning_fee_level2: '5.00', dunning_fee_level3: '10.00',
};

const emptyProfile: Profile = {
  first_name: '', last_name: '', email: '', phone: '', title: '',
  business_name: '', address_line1: '', address_line2: '', zip_code: '', city: '', country: 'Deutschland',
  tax_number: '', vat_id: '', bank_name: '', iban: '', bic: '',
  smtp_host: '', smtp_port: '587', smtp_user: '', smtp_password: '', smtp_from_email: '', smtp_from_name: '',
};

export default function Settings() {
  const { showToast } = useToast();
  const { subscription } = useAuth();
  const currentPlan = subscription?.plan_type || 'free';
  const [activeTab, setActiveTab] = useState<'geschaeft' | 'vorlagen' | 'rechnungen' | 'kalender' | 'rechtliches' | 'pakete' | 'notizvorlagen' | 'sicherheit' | 'team'>('geschaeft');
  useTour('settings', settingsTourSteps, { delay: 800 });
  const [form, setForm] = useState<Profile>(emptyProfile);
  const [invSettings, setInvSettings] = useState<InvoiceSettings>(emptyInvoiceSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [invSaving, setInvSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  useEffect(() => {
    authApi.me().then((res) => {
      const d = res.data;
      setForm({
        first_name: d.first_name || '', last_name: d.last_name || '', email: d.email || '',
        phone: d.phone || '', title: d.title || '', business_name: d.business_name || '',
        address_line1: d.address_line1 || '', address_line2: d.address_line2 || '',
        zip_code: d.zip_code || '', city: d.city || '', country: d.country || 'Deutschland',
        tax_number: d.tax_number || '', vat_id: d.vat_id || '',
        bank_name: d.bank_name || '', iban: d.iban || '', bic: d.bic || '',
        smtp_host: d.smtp_host || '', smtp_port: d.smtp_port ? String(d.smtp_port) : '587',
        smtp_user: d.smtp_user || '', smtp_password: '',
        smtp_from_email: d.smtp_from_email || '', smtp_from_name: d.smtp_from_name || '',
      });
      setInvSettings({
        is_kleinunternehmer: d.is_kleinunternehmer || false, company_type: d.company_type || '',
        invoice_prefix: d.invoice_prefix || 'RE', offer_prefix: d.offer_prefix || 'AN',
        next_invoice_number: d.next_invoice_number ? String(d.next_invoice_number) : '1',
        next_offer_number: d.next_offer_number ? String(d.next_offer_number) : '1',
        default_payment_days: d.default_payment_days ? String(d.default_payment_days) : '14',
        default_intro_text: d.default_intro_text || '', default_outro_text: d.default_outro_text || '',
        dunning_grace_days: d.dunning_grace_days != null ? String(d.dunning_grace_days) : '3',
        dunning_reminder_days: d.dunning_reminder_days != null ? String(d.dunning_reminder_days) : '7',
        dunning_level1_days: d.dunning_level1_days != null ? String(d.dunning_level1_days) : '14',
        dunning_level2_days: d.dunning_level2_days != null ? String(d.dunning_level2_days) : '28',
        dunning_level3_days: d.dunning_level3_days != null ? String(d.dunning_level3_days) : '42',
        dunning_fee_level2: d.dunning_fee_level2 != null ? String(d.dunning_fee_level2) : '5.00',
        dunning_fee_level3: d.dunning_fee_level3 != null ? String(d.dunning_fee_level3) : '10.00',
      });
    }).finally(() => setLoading(false));
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload: any = {
        first_name: form.first_name || undefined, last_name: form.last_name || undefined,
        phone: form.phone || undefined, title: form.title || undefined,
        business_name: form.business_name || undefined, address_line1: form.address_line1 || undefined,
        address_line2: form.address_line2 || undefined, zip_code: form.zip_code || undefined,
        city: form.city || undefined, country: form.country || undefined,
        tax_number: form.tax_number || undefined, vat_id: form.vat_id || undefined,
        bank_name: form.bank_name || undefined, iban: form.iban || undefined, bic: form.bic || undefined,
        smtp_host: form.smtp_host || undefined, smtp_port: form.smtp_port ? parseInt(form.smtp_port) : undefined,
        smtp_user: form.smtp_user || undefined, smtp_from_email: form.smtp_from_email || undefined,
        smtp_from_name: form.smtp_from_name || undefined,
      };
      if (form.smtp_password) payload.smtp_password = form.smtp_password;
      await authApi.updateProfile(payload);
      showToast('Geschäftsdaten gespeichert', 'success');
    } catch { showToast('Fehler beim Speichern', 'error'); }
    finally { setSaving(false); }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try { await authApi.testEmail(); showToast('Test-E-Mail erfolgreich gesendet!', 'success'); }
    catch (err: any) { showToast(err.response?.data?.detail || 'Test fehlgeschlagen', 'error'); }
    finally { setTestingEmail(false); }
  };

  return (
    <div>
      <div className="mb-24">
        <h1>Einstellungen</h1>
        <p className="text-muted text-sm">Geschäftsdaten, Rechnungen & Steuern, Vertragsvorlagen, Verfügbarkeit</p>
      </div>

      <div className="category-chips mb-24" data-tour="settings-tabs">
        <button className={`chip ${activeTab === 'geschaeft' ? 'chip-active' : ''}`} onClick={() => setActiveTab('geschaeft')} data-tour="settings-profile">
          Geschäftsdaten
        </button>
        <button className={`chip ${activeTab === 'pakete' ? 'chip-active' : ''}`} onClick={() => setActiveTab('pakete')}>
          <Package size={14} style={{ marginRight: 4 }} /> Coaching-Pakete
        </button>
        <button className={`chip ${activeTab === 'rechnungen' ? 'chip-active' : ''}`} onClick={() => setActiveTab('rechnungen')}>
          <Euro size={14} style={{ marginRight: 4 }} /> Rechnungen & Steuern
        </button>
        <button className={`chip ${activeTab === 'vorlagen' ? 'chip-active' : ''}`} onClick={() => setActiveTab('vorlagen')}>
          <FileText size={14} style={{ marginRight: 4 }} /> Vertragsvorlagen
        </button>
        <button className={`chip ${activeTab === 'notizvorlagen' ? 'chip-active' : ''}`} onClick={() => setActiveTab('notizvorlagen')}>
          <ClipboardList size={14} style={{ marginRight: 4 }} /> Notiz-Vorlagen
        </button>
        <button className={`chip ${activeTab === 'kalender' ? 'chip-active' : ''}`} onClick={() => setActiveTab('kalender')}>
          <Calendar size={14} style={{ marginRight: 4 }} /> Verfügbarkeit & Buchung
        </button>
        <button className={`chip ${activeTab === 'rechtliches' ? 'chip-active' : ''}`} onClick={() => setActiveTab('rechtliches')}>
          <Scale size={14} style={{ marginRight: 4 }} /> Rechtliches
        </button>
        <button className={`chip ${activeTab === 'sicherheit' ? 'chip-active' : ''}`} onClick={() => setActiveTab('sicherheit')}>
          <ShieldCheck size={14} style={{ marginRight: 4 }} /> Sicherheit
        </button>
        <button className={`chip ${activeTab === 'team' ? 'chip-active' : ''}`} onClick={() => setActiveTab('team')}>
          <Users size={14} style={{ marginRight: 4 }} /> Team
        </button>
      </div>

      {loading && (activeTab === 'geschaeft' || activeTab === 'rechnungen') && (
        <div className="card" style={{ maxWidth: 720, padding: 32 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <Skeleton width="40%" height={14} />
            <div style={{ display: 'flex', gap: 16 }}>
              <Skeleton height={38} />
              <Skeleton height={38} />
            </div>
            <Skeleton width="40%" height={14} />
            <Skeleton height={38} />
            <div style={{ display: 'flex', gap: 16 }}>
              <Skeleton height={38} />
              <Skeleton height={38} />
            </div>
            <Skeleton width={180} height={40} borderRadius={8} />
          </div>
        </div>
      )}

      {activeTab === 'pakete' && <PackageTemplatesEditor />}
      {activeTab === 'vorlagen' && <VertragsvorlagenEditor />}
      {activeTab === 'notizvorlagen' && <NoteTemplateEditor />}
      {activeTab === 'kalender' && <AvailabilitySettings />}
      {activeTab === 'rechtliches' && <LegalSettings />}
      {activeTab === 'sicherheit' && <SecuritySettings />}
      {activeTab === 'team' && (
        <FeatureGate
          requiredPlan="team_s"
          currentPlan={currentPlan}
          featureName="Team-Verwaltung"
          featureDescription="Laden Sie Kollegen ein, verwalten Sie Rollen und Berechtigungen und arbeiten Sie gemeinsam an Klienten."
        >
          <TeamManagement />
        </FeatureGate>
      )}

      {/* Rechnungen & Steuern Tab */}
      {activeTab === 'rechnungen' && !loading && (
        <form onSubmit={async (e) => {
          e.preventDefault(); setInvSaving(true);
          try {
            await authApi.updateProfile({
              is_kleinunternehmer: invSettings.is_kleinunternehmer,
              company_type: invSettings.company_type || undefined,
              invoice_prefix: invSettings.invoice_prefix || 'RE', offer_prefix: invSettings.offer_prefix || 'AN',
              next_invoice_number: parseInt(invSettings.next_invoice_number) || 1,
              next_offer_number: parseInt(invSettings.next_offer_number) || 1,
              default_payment_days: parseInt(invSettings.default_payment_days) || 14,
              default_intro_text: invSettings.default_intro_text || undefined,
              default_outro_text: invSettings.default_outro_text || undefined,
              dunning_grace_days: parseInt(invSettings.dunning_grace_days) || 3,
              dunning_reminder_days: parseInt(invSettings.dunning_reminder_days) || 7,
              dunning_level1_days: parseInt(invSettings.dunning_level1_days) || 14,
              dunning_level2_days: parseInt(invSettings.dunning_level2_days) || 28,
              dunning_level3_days: parseInt(invSettings.dunning_level3_days) || 42,
              dunning_fee_level2: parseFloat(invSettings.dunning_fee_level2) || 5.00,
              dunning_fee_level3: parseFloat(invSettings.dunning_fee_level3) || 10.00,
            });
            showToast('Rechnungseinstellungen gespeichert', 'success');
          } catch { showToast('Fehler beim Speichern', 'error'); }
          finally { setInvSaving(false); }
        }}>
          <div className="card" style={{ maxWidth: 720, padding: 32 }}>

            <div className="settings-section">
              <h3>Unternehmensform & Steuerstatus</h3>
              <div className="form-group">
                <label>Unternehmensform</label>
                <select value={invSettings.company_type} onChange={(e) => { setInvSettings({ ...invSettings, company_type: e.target.value }); }}>
                  <option value="">— Bitte wählen —</option>
                  <option value="einzelunternehmen">Einzelunternehmen</option>
                  <option value="freiberufler">Freiberufler</option>
                  <option value="gbr">GbR</option><option value="gmbh">GmbH</option>
                  <option value="ug">UG (haftungsbeschränkt)</option><option value="kg">KG</option>
                  <option value="ohg">OHG</option><option value="verein">Verein</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: invSettings.is_kleinunternehmer ? '#e8f5e9' : 'var(--bg)', borderRadius: 8, marginTop: 12 }}>
                <input type="checkbox" id="kleinunternehmer" checked={invSettings.is_kleinunternehmer}
                  onChange={(e) => { setInvSettings({ ...invSettings, is_kleinunternehmer: e.target.checked }); }} style={{ width: 20, height: 20 }} />
                <div>
                  <label htmlFor="kleinunternehmer" style={{ fontWeight: 600, cursor: 'pointer', marginBottom: 0 }}>Kleinunternehmerregelung (§ 19 UStG)</label>
                  <p className="text-muted text-sm" style={{ margin: 0 }}>Wenn aktiviert, wird keine Umsatzsteuer ausgewiesen.</p>
                </div>
              </div>
            </div>

            <div className="settings-section">
              <h3>Nummernkreise</h3>
              <p className="text-muted text-sm" style={{ marginBottom: 12 }}>Format: PRÄFIX-JAHR-NNNN (z.B. RE-2026-0001)</p>
              <div className="form-row">
                <div className="form-group"><label>Rechnungs-Präfix</label><input value={invSettings.invoice_prefix} onChange={(e) => { setInvSettings({ ...invSettings, invoice_prefix: e.target.value }); }} placeholder="RE" maxLength={10} /></div>
                <div className="form-group"><label>Angebots-Präfix</label><input value={invSettings.offer_prefix} onChange={(e) => { setInvSettings({ ...invSettings, offer_prefix: e.target.value }); }} placeholder="AN" maxLength={10} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Nächste Rechnungsnummer</label><input type="number" value={invSettings.next_invoice_number} onChange={(e) => { setInvSettings({ ...invSettings, next_invoice_number: e.target.value }); }} min={1} /></div>
                <div className="form-group"><label>Nächste Angebotsnummer</label><input type="number" value={invSettings.next_offer_number} onChange={(e) => { setInvSettings({ ...invSettings, next_offer_number: e.target.value }); }} min={1} /></div>
              </div>
            </div>

            <div className="settings-section">
              <h3>Standard-Zahlungsziel</h3>
              <div className="form-group">
                <label>Zahlungsziel (Tage)</label>
                <input type="number" value={invSettings.default_payment_days} onChange={(e) => { setInvSettings({ ...invSettings, default_payment_days: e.target.value }); }} min={1} max={365} style={{ maxWidth: 120 }} />
              </div>
            </div>

            <div className="settings-section">
              <h3>Standardtexte für Angebote</h3>
              <div className="form-group"><label>Einleitungstext</label><textarea value={invSettings.default_intro_text} onChange={(e) => { setInvSettings({ ...invSettings, default_intro_text: e.target.value }); }} rows={3} /></div>
              <div className="form-group"><label>Schlusstext</label><textarea value={invSettings.default_outro_text} onChange={(e) => { setInvSettings({ ...invSettings, default_outro_text: e.target.value }); }} rows={3} /></div>
            </div>

            <div className="settings-section">
              <h3>Mahnwesen</h3>
              <p className="text-muted text-sm" style={{ marginBottom: 12 }}>Fristen und Gebühren für die automatische Mahnstufenberechnung.</p>

              <div className="form-group" style={{ marginBottom: 16 }}>
                <label>Karenzzeit nach Fälligkeit (Tage)</label>
                <input type="number" value={invSettings.dunning_grace_days} onChange={(e) => setInvSettings({ ...invSettings, dunning_grace_days: e.target.value })} min={0} max={30} style={{ maxWidth: 120 }} />
                <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>Tage nach Fälligkeit, bevor eine Rechnung als überfällig gilt.</p>
              </div>

              <h4 style={{ fontSize: '0.9rem', marginBottom: 8 }}>Zahlungsfristen (Tage ab heute)</h4>
              <div className="form-row" style={{ marginBottom: 16 }}>
                <div className="form-group"><label>Erinnerung</label><input type="number" value={invSettings.dunning_reminder_days} onChange={(e) => setInvSettings({ ...invSettings, dunning_reminder_days: e.target.value })} min={1} /></div>
                <div className="form-group"><label>1. Mahnung</label><input type="number" value={invSettings.dunning_level1_days} onChange={(e) => setInvSettings({ ...invSettings, dunning_level1_days: e.target.value })} min={1} /></div>
                <div className="form-group"><label>2. Mahnung</label><input type="number" value={invSettings.dunning_level2_days} onChange={(e) => setInvSettings({ ...invSettings, dunning_level2_days: e.target.value })} min={1} /></div>
                <div className="form-group"><label>3. Mahnung</label><input type="number" value={invSettings.dunning_level3_days} onChange={(e) => setInvSettings({ ...invSettings, dunning_level3_days: e.target.value })} min={1} /></div>
              </div>

              <h4 style={{ fontSize: '0.9rem', marginBottom: 8 }}>Mahngebühren (EUR)</h4>
              <div className="form-row">
                <div className="form-group"><label>Ab 2. Mahnung</label><input type="number" step="0.50" value={invSettings.dunning_fee_level2} onChange={(e) => setInvSettings({ ...invSettings, dunning_fee_level2: e.target.value })} min={0} /></div>
                <div className="form-group"><label>Ab 3. Mahnung</label><input type="number" step="0.50" value={invSettings.dunning_fee_level3} onChange={(e) => setInvSettings({ ...invSettings, dunning_fee_level3: e.target.value })} min={0} /></div>
              </div>
              <p className="text-muted text-sm" style={{ marginTop: 4 }}>Erinnerung und 1. Mahnung sind gebührenfrei.</p>
            </div>

            <button type="submit" className="btn btn-primary" disabled={invSaving}><Save size={18} /> {invSaving ? 'Speichern...' : 'Änderungen speichern'}</button>
          </div>
        </form>
      )}

      {/* Geschäftsdaten Tab */}
      {activeTab === 'geschaeft' && !loading && (
        <form onSubmit={handleSubmit}>
          <div className="card" style={{ maxWidth: 720, padding: 32 }}>

            <div className="settings-section">
              <h3>Geschäftsadresse</h3>
              <div className="form-group"><label>Firmenname / Praxisname</label><input name="business_name" value={form.business_name} onChange={handleChange} placeholder="z.B. Coaching-Praxis Mustermann" /></div>
              <div className="form-group"><label>Straße + Hausnummer</label><input name="address_line1" value={form.address_line1} onChange={handleChange} /></div>
              <div className="form-group"><label>Adresszusatz</label><input name="address_line2" value={form.address_line2} onChange={handleChange} /></div>
              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}><label>PLZ</label><input name="zip_code" value={form.zip_code} onChange={handleChange} /></div>
                <div className="form-group" style={{ flex: 2 }}><label>Stadt</label><input name="city" value={form.city} onChange={handleChange} /></div>
              </div>
            </div>

            <div className="settings-section">
              <h3>Steuerliche Angaben</h3>
              <div className="form-row">
                <div className="form-group"><label>Steuernummer</label><input name="tax_number" value={form.tax_number} onChange={handleChange} /></div>
                <div className="form-group"><label>USt-IdNr.</label><input name="vat_id" value={form.vat_id} onChange={handleChange} /></div>
              </div>
            </div>

            <div className="settings-section">
              <h3>Bankverbindung</h3>
              <div className="form-group"><label>Bank</label><input name="bank_name" value={form.bank_name} onChange={handleChange} /></div>
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}><label>IBAN</label><input name="iban" value={form.iban} onChange={handleChange} /></div>
                <div className="form-group" style={{ flex: 1 }}><label>BIC</label><input name="bic" value={form.bic} onChange={handleChange} /></div>
              </div>
            </div>

            <div className="settings-section">
              <h3><Mail size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />E-Mail-Einstellungen (SMTP)</h3>
              <p className="text-muted text-sm" style={{ marginBottom: 12 }}>Rechnungen und Zahlungserinnerungen direkt per E-Mail senden.</p>
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}><label>SMTP-Server</label><input name="smtp_host" value={form.smtp_host} onChange={handleChange} placeholder="smtp.gmail.com" /></div>
                <div className="form-group" style={{ flex: 1 }}><label>Port</label><input name="smtp_port" value={form.smtp_port} onChange={handleChange} type="number" /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Benutzername</label><input name="smtp_user" value={form.smtp_user} onChange={handleChange} /></div>
                <div className="form-group"><label>Passwort</label><input name="smtp_password" value={form.smtp_password} onChange={handleChange} type="password" /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Absender-E-Mail</label><input name="smtp_from_email" value={form.smtp_from_email} onChange={handleChange} /></div>
                <div className="form-group"><label>Absender-Name</label><input name="smtp_from_name" value={form.smtp_from_name} onChange={handleChange} /></div>
              </div>
              <button type="button" className="btn btn-secondary" onClick={handleTestEmail} disabled={testingEmail || !form.smtp_host || !form.smtp_user}>
                <Send size={16} /> {testingEmail ? 'Sende...' : 'Test-E-Mail senden'}
              </button>
            </div>

            <button type="submit" className="btn btn-primary" disabled={saving}><Save size={18} /> {saving ? 'Speichern...' : 'Änderungen speichern'}</button>
          </div>
        </form>
      )}
    </div>
  );
}
